import { useState, type ChangeEvent, type DragEvent, type FormEvent } from 'react';
import { DraftNotice } from '../../components/DraftNotice';
import { OptionChips } from '../../components/OptionChips';
import { planErrorMessage } from '../../components/plan/planErrors';
import { ApiError } from '../../lib/apiClient';
import { clearDraft, knowledgeBaseDraftKey, restoreDraft } from '../../lib/draftStorage';
import { IMAGE_ACCEPT_ATTRIBUTE, uploadImageToAssets, validateImageFile } from '../../lib/storage';
import { useDraft } from '../../lib/useDraft';
import type { InspirationRead, InspirationTag } from '../../types/api';
import { AssetPreview } from './AssetPreview';
import { inspirationsApi } from './knowledgeBaseApi';
import { SectionShell } from './SectionShell';
import { useCollection } from './useCollection';
import { useSectionCapacity } from './useSectionCapacity';

/** The server's closed tag set, in the handoff's order, with the labels it shows. */
const TAG_OPTIONS: readonly { value: InspirationTag; label: string }[] = [
  { value: 'minimal', label: 'Minimal' },
  { value: 'bold_colour', label: 'Bold colour' },
  { value: 'photo_led', label: 'Photo-led' },
  { value: 'illustrated', label: 'Illustrated' },
  { value: 'dark_moody', label: 'Dark & moody' },
  { value: 'playful_type', label: 'Playful type' },
  { value: 'white_space', label: 'Lots of white space' },
  { value: 'hand_made', label: 'Hand-made feel' },
];

function tagLabel(tag: InspirationTag): string {
  return TAG_OPTIONS.find((option) => option.value === tag)?.label ?? tag;
}

/** `InspirationRead.name` and `.note` caps, as the server checks them. */
const NAME_MAX = 200;
const NOTE_MAX = 500;

/** Also the subject of the storage-cap message. */
const SECTION_TITLE = 'Inspiration';

/** Autosaved shape. The chosen file is not in it: a `File` cannot be stored. */
interface InspirationDraft {
  name: string;
  note: string;
  tags: InspirationTag[];
}

const EMPTY_DRAFT: InspirationDraft = { name: '', note: '', tags: [] };

function writeErrorMessage(caught: unknown, options: Parameters<typeof planErrorMessage>[2]) {
  if (caught instanceof ApiError && caught.code === 'INSPIRATION_LIMIT_REACHED') {
    return 'You have 50 inspirations, the most a brand can keep. Remove one to add another.';
  }
  return planErrorMessage(caught, 'That inspiration did not save. Try again.', options);
}

interface InspirationFormProps {
  businessId: string;
  onAdded: () => void;
  create: (body: {
    name: string;
    note: string | null;
    tags: InspirationTag[];
    image_path: string | null;
  }) => Promise<InspirationRead>;
}

function InspirationForm({ businessId, create, onAdded }: InspirationFormProps) {
  const { planName, refreshCapacity } = useSectionCapacity(businessId, 'storage_bytes');
  const draftKey = knowledgeBaseDraftKey('inspiration', businessId, null);
  const [restored] = useState(() => restoreDraft(draftKey, EMPTY_DRAFT));
  const [form, setForm] = useState<InspirationDraft>(restored.value);
  const [restoredAt, setRestoredAt] = useState<string | null>(restored.savedAt);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useDraft(draftKey, form, EMPTY_DRAFT);

  function pickFile(candidate: File | undefined) {
    if (!candidate) return;
    const rejected = validateImageFile(candidate);
    setNotice(null);
    setError(rejected);
    setFile(rejected ? null : candidate);
  }

  function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    pickFile(event.target.files?.[0]);
    event.target.value = '';
  }

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault();
    setIsDragging(false);
    pickFile(event.dataTransfer.files[0]);
  }

  function toggleTag(value: string) {
    const tag = value as InspirationTag;
    setForm((current) => ({
      ...current,
      tags: current.tags.includes(tag)
        ? current.tags.filter((entry) => entry !== tag)
        : [...current.tags, tag],
    }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const name = form.name.trim();
    if (!name) return;

    setIsSaving(true);
    setError(null);
    setNotice(null);
    try {
      // Signed upload first (the server keys it under `inspo/`), then the row.
      const imagePath = file ? await uploadImageToAssets(businessId, file, 'inspirations') : null;
      await create({
        name,
        note: form.note.trim() || null,
        tags: form.tags,
        image_path: imagePath,
      });
      clearDraft(draftKey);
      setForm(EMPTY_DRAFT);
      setFile(null);
      setRestoredAt(null);
      setNotice('Inspiration added.');
      onAdded();
    } catch (caught) {
      setError(writeErrorMessage(caught, { plan: planName, subject: SECTION_TITLE }));
    } finally {
      setIsSaving(false);
      // Bytes may have moved even when the row was refused.
      if (file) refreshCapacity();
    }
  }

  return (
    <form
      className="form kb-form kb-form--add"
      onSubmit={(event) => void handleSubmit(event)}
      noValidate
    >
      {restoredAt ? (
        <DraftNotice savedAt={restoredAt} onDismiss={() => setRestoredAt(null)} />
      ) : null}

      <div className="kb-form__grid">
        <label className="field">
          <span className="field__label">Where is it from?</span>
          <input
            className="field__input"
            type="text"
            required
            maxLength={NAME_MAX}
            placeholder="e.g. Aesop's Instagram, or paste a link"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
        </label>
        <label className="field">
          <span className="field__label">What do you like about it?</span>
          <input
            className="field__input"
            type="text"
            maxLength={NOTE_MAX}
            placeholder="e.g. lots of space, soft natural light"
            value={form.note}
            onChange={(event) => setForm({ ...form, note: event.target.value })}
          />
        </label>
      </div>

      <OptionChips
        legend="Tap anything that fits"
        options={TAG_OPTIONS}
        selected={form.tags}
        onToggle={toggleTag}
      />

      <label
        className={isDragging ? 'inspo-drop inspo-drop--over' : 'inspo-drop'}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <span className="inspo-drop__text">
          {file ? file.name : 'Choose an image, or drop one here'}
        </span>
        <span className="field__hint">Optional. PNG, JPEG, WebP or GIF, under 5 MB.</span>
        <input
          className="visually-hidden"
          type="file"
          accept={IMAGE_ACCEPT_ATTRIBUTE}
          disabled={isSaving}
          onChange={handleFileChange}
        />
      </label>

      {error ? (
        <p className="form__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="kb-form__actions">
        <button
          className="button button--primary"
          type="submit"
          disabled={isSaving || form.name.trim().length === 0}
        >
          {isSaving ? 'Saving' : file ? 'Upload image' : 'Add inspiration'}
        </button>
        <span className="field__hint" role="status">
          {notice}
        </span>
      </div>
    </form>
  );
}

/**
 * Inspiration: pictures the user likes from any brand, so the designer learns
 * a taste, never to copy. Stored and counted toward readiness in v1; nothing
 * reads them into a prompt yet.
 *
 * An image is optional. When one is chosen it goes to the private `assets`
 * bucket through the server's signed `inspirations/upload-url` (which keys it
 * under `{business_id}/inspo/` and counts its bytes against the storage cap),
 * then the row is posted with that path. A pasted link is stored as the name;
 * no preview is fetched for it. No edit in v1: add or remove.
 */
export function InspirationSection({ businessId }: { businessId: string }) {
  const collection = useCollection(inspirationsApi, businessId);
  const { refreshCapacity } = useSectionCapacity(businessId, 'storage_bytes');

  function startDelete(item: InspirationRead) {
    collection.removeWithUndo({
      id: item.id,
      label: item.name,
      failureMessage: 'That inspiration could not be removed.',
      onRemoved: refreshCapacity,
    });
  }

  return (
    <SectionShell
      title={SECTION_TITLE}
      lede="Pictures of visuals you like, from any brand. We use them to understand the style you want — never to copy."
      aside={
        <InspirationForm
          businessId={businessId}
          create={collection.create}
          onAdded={refreshCapacity}
        />
      }
      isLoading={collection.isLoading}
      error={collection.error ?? collection.deleteError}
      isEmpty={collection.items.length === 0}
      emptyMessage="Nothing yet. Three or four images are plenty."
    >
      <ul className="inspo-grid">
        {collection.items.map((item) => (
          <li className="asset-card inspo-card" key={item.id}>
            {item.image_path ? (
              <AssetPreview storagePath={item.image_path} label={item.name} />
            ) : (
              <div className="asset-preview asset-preview--thumb" aria-hidden="true" />
            )}
            <div className="inspo-card__head">
              <span className="inspo-card__name">{item.name}</span>
              <button
                className="button button--ghost inspo-card__remove"
                type="button"
                aria-label={`Remove ${item.name}`}
                onClick={() => startDelete(item)}
              >
                ×
              </button>
            </div>
            {item.note ? <p className="inspo-card__note">“{item.note}”</p> : null}
            {item.tags.length > 0 ? (
              <ul className="token-list token-list--static">
                {item.tags.map((tag) => (
                  <li className="token" key={tag}>
                    {tagLabel(tag)}
                  </li>
                ))}
              </ul>
            ) : null}
          </li>
        ))}
      </ul>
    </SectionShell>
  );
}
